import { Table, Stack, Text, ActionIcon, useMantineTheme, Box, Group, Menu, Image, Tooltip, Loader, Center, Badge } from "@mantine/core";
import { MaterialSymbol } from "react-material-symbols";
import { OrderDTO } from "../../../interfaces/OrderInterfaces";
import { ArticleDetails } from "../../../interfaces/response/ArticleDetails";
import classes from "./OrderTable.module.css";
import { useMemo, memo, useState } from "react";
import MexalOrderCreationModal from "./MexalOrderCreationModal";
import MiraklAcceptOrderModal from "./MiraklAcceptOrderModal";

interface OrderTableProps {
    orders: OrderDTO[];
    loading: boolean;
    articles?: ArticleDetails[];
    mexalDocuments?: Record<string, string>; // orderId -> mexalDocumentId (es. "OX1/2024")
}

const OrderTable = ({ orders, loading, articles, mexalDocuments }: OrderTableProps) => {
    const theme = useMantineTheme();
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
    const [mexalModalOpen, setMexalModalOpen] = useState(false);
    const [orderForMexal, setOrderForMexal] = useState<OrderDTO | null>(null);
    
    // Accept order modal state
    const [acceptModalOpen, setAcceptModalOpen] = useState(false);
    const [orderForAccept, setOrderForAccept] = useState<OrderDTO | null>(null);

    const handleCreateMexalOrder = (order: OrderDTO) => {
        setOrderForMexal(order);
        setMexalModalOpen(true);
    };

    const handleMexalModalClose = () => {
        setMexalModalOpen(false);
        setOrderForMexal(null);
    };

    const handleAcceptOrder = (order: OrderDTO) => {
        setOrderForAccept(order);
        setAcceptModalOpen(true);
    };

    const handleAcceptModalClose = () => {
        setAcceptModalOpen(false);
        setOrderForAccept(null);
    };

    // Check if order can be accepted (Mirakl marketplace + WAITING_ACCEPTANCE status)
    const canAcceptOrder = (order: OrderDTO) => {
        const isMirakl = ['MEDIAWORLD', 'LEROY_MERLIN', 'LEROYMERLIN', 'EPRICE'].includes(order.marketplace);
        const isWaitingAcceptance = order.status === 'WAITING_ACCEPTANCE';
        return isMirakl && isWaitingAcceptance;
    };

    const articlesMap = useMemo(() => {
        const map = new Map<string, ArticleDetails>();
        articles?.forEach(article => {
            map.set(article.headerArticleDetails.code, article);
        });
        return map;
    }, [articles]);

    const formatStatus = (status: string) => {
        if (!status) return 'N/A';
        
        const statusMap: Record<string, string> = {
            'STAGING': 'In Preparazione',
            'WAITING_ACCEPTANCE': 'In Attesa di Accettazione',
            'WAITING_DEBIT': 'In Attesa di Addebito',
            'WAITING_DEBIT_PAYMENT': 'In Attesa di Pagamento',
            'PENDING': 'In Attesa',
            'SHIPPING': 'In Attesa di Spedizione',
            'SHIPPED': 'Spedito',
            'COMPLETED': 'Completato',
            'RECEIVED': 'Ricevuto',
            'CLOSED': 'Chiuso',
            'CANCELED': 'Annullato',
            'CANCELLED': 'Annullato',
            'REFUNDED': 'Rimborsato',
            'REFUSED': 'Rifiutato',
            'UNKNOWN': 'Sconosciuto'
        };
        
        return statusMap[status.toUpperCase()] || status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    };

    const getStatusColor = (status: string) => {
        const s = status?.toUpperCase();
        if (s?.includes('SHIPPED') || s?.includes('SPEDITO') || s?.includes('COMPLETE')) return 'teal';
        if (s?.includes('CANCEL') || s?.includes('ANNULLATO') || s?.includes('REFUND')) return 'pink';
        if (s?.includes('PENDING') || s?.includes('ATTESA') || s?.includes('WAITING') || s?.includes('STAGING')) return 'orange';
        if (s?.includes('SHIPPING') || s?.includes('SPEDIZIONE')) return 'cyan';
        return 'gray';
    };

    const getMarketplaceColor = (marketplace: string) => {
        const colors: Record<string, string> = {
            'EBAY': 'indigo',
            'MEDIAWORLD': 'grape',
            'LEROY_MERLIN': 'cyan',
            'LEROYMERLIN': 'cyan',
            'LeroyMerlin': 'cyan',
            'EPRICE': 'yellow'
        };
        return colors[marketplace] || 'gray';
    };

    const formatMarketplaceName = (marketplace: string) => {
        const nameMap: Record<string, string> = {
            'EBAY': 'eBay',
            'MEDIAWORLD': 'MediaWorld',
            'LEROY_MERLIN': 'Leroy Merlin',
            'LEROYMERLIN': 'Leroy Merlin',
            'EPRICE': 'ePrice'
        };
        return nameMap[marketplace] || marketplace;
    };

    const handleRowClick = (orderId: string) => {
        setSelectedOrderId(prev => prev === orderId ? null : orderId);
    };

    if (loading) {
        return (
            <Center py="xl">
                <Loader size="md" type="dots" />
            </Center>
        );
    }

    return (
        <div className={classes.tableWrapper}>
            <Table className={classes.table} style={{ tableLayout: 'fixed', width: '100%' }}>
                <Table.Thead className={classes.tableHeader}>
                    <Table.Tr>
                        <Table.Th style={{ width: 40 }}></Table.Th>
                        <Table.Th style={{ width: 100 }}>Data</Table.Th>
                        <Table.Th style={{ width: 200 }}>ID Ordine</Table.Th>
                        <Table.Th style={{ width: 180 }}>Cliente</Table.Th>
                        <Table.Th style={{ width: 280 }}>Articoli</Table.Th>
                        <Table.Th style={{ width: 100 }}>Totale</Table.Th>
                        <Table.Th style={{ width: 160 }}>Stato</Table.Th>
                        <Table.Th style={{ width: 120 }}>Doc. Mexal</Table.Th>
                        <Table.Th style={{ width: 50 }}></Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {orders.map((order) => {
                        const isExpanded = selectedOrderId === order.orderId;
                        const firstItem = order.items?.[0];
                        const article = firstItem ? articlesMap.get(firstItem.sku) : null;
                        const imageUrl = article?.headerArticleDetails?.imageLink;
                        const itemsCount = order.items?.length || 0;
                        const total = order.items?.reduce((acc, item) => acc + (item.price * item.quantity), 0) || 0;
                        const currency = order.items?.[0]?.currency || 'EUR';
                        
                        // Verifica se l'ordine è di oggi
                        const orderDate = new Date(order.creationDate);
                        const today = new Date();
                        const isToday = orderDate.toDateString() === today.toDateString();

                        return (
                            <>
                                <Table.Tr 
                                    key={order.orderId}
                                    className={`${classes.orderRow} ${isExpanded ? classes.expanded : ''}`}
                                    onClick={() => handleRowClick(order.orderId)}
                                    style={{ 
                                        borderLeft: `3px solid var(--mantine-color-${getMarketplaceColor(order.marketplace)}-5)`,
                                        backgroundColor: isToday ? `var(--mantine-color-${getMarketplaceColor(order.marketplace)}-0)` : undefined
                                    }}
                                >
                                    {/* Icona espansione */}
                                    <Table.Td style={{ width: 40, padding: '12px 8px' }}>
                                        <div className={classes.expandIcon}>
                                            <MaterialSymbol 
                                                icon={isExpanded ? "expand_less" : "expand_more"} 
                                                size={20} 
                                            />
                                        </div>
                                    </Table.Td>

                                    {/* Data */}
                                    <Table.Td style={{ width: 100 }}>
                                        <Stack gap={0}>
                                            <Text size="sm" fw={600} c="dark.9">
                                                {new Date(order.creationDate).toLocaleDateString('it-IT')}
                                            </Text>
                                            <Text size="xs" c="gray.7" fw={500}>
                                                {new Date(order.creationDate).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                                            </Text>
                                        </Stack>
                                    </Table.Td>

                                    {/* ID Ordine */}
                                    <Table.Td style={{ width: 200 }}>
                                        <Stack gap={4}>
                                            <Text size="sm" fw={600} style={{fontFamily: 'monospace', wordBreak: 'break-all'}} c="dark.9">{order.orderId}</Text>
                                            <Badge 
                                                size="sm" 
                                                variant="light" 
                                                color={getMarketplaceColor(order.marketplace)}
                                                style={{ maxWidth: 'fit-content' }}
                                            >
                                                {formatMarketplaceName(order.marketplace)}
                                            </Badge>
                                        </Stack>
                                    </Table.Td>

                                    {/* Cliente */}
                                    <Table.Td style={{ width: 180 }}>
                                        <Stack gap={2}>
                                            <Text size="sm" fw={600} c="dark.9" lineClamp={1}>
                                                {order.customer?.firstName} {order.customer?.lastName}
                                            </Text>
                                            {order.customer?.email && (
                                                <Text size="xs" c="dark.4" fw={500} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.customer.email}</Text>
                                            )}
                                        </Stack>
                                    </Table.Td>

                                    {/* Articoli */}
                                    <Table.Td style={{ width: 280 }}>
                                        {firstItem ? (
                                            <Group gap="sm" wrap="nowrap">
                                                <Box style={{ width: 56, height: 56, flexShrink: 0 }}>
                                                    {imageUrl ? (
                                                        <Image
                                                            src={imageUrl}
                                                            alt={firstItem.productName}
                                                            width={56}
                                                            height={56}
                                                            fit="contain"
                                                            style={{ borderRadius: '6px' }}
                                                        />
                                                    ) : (
                                                        <Box style={{ 
                                                            width: 56, height: 56, 
                                                            borderRadius: '6px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <MaterialSymbol icon="image" size={22} color={theme.colors.gray[5]} />
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                                                    <Text size="sm" fw={600} c="dark.9" lineClamp={1}>{firstItem.productName}</Text>
                                                    <Group gap={4}>
                                                        <Text size="xs" c="gray.8" fw={600}>SKU: {firstItem.sku}</Text>
                                                        {itemsCount > 1 && (
                                                            <Text size="xs" c="dark.5" fw={600}>+{itemsCount - 1} altri</Text>
                                                        )}
                                                        {order.hasQuantityWarning && (
                                                            <Tooltip label="Quantità insufficiente" withArrow>
                                                                <MaterialSymbol icon="warning" size={14} color={theme.colors.red[7]} />
                                                            </Tooltip>
                                                        )}
                                                    </Group>
                                                </Stack>
                                            </Group>
                                        ) : (
                                            <Text size="xs" c="gray.7" fw={500}>Nessun articolo</Text>
                                        )}
                                    </Table.Td>

                                    {/* Totale */}
                                    <Table.Td style={{ width: 100 }}>
                                        <Text size="sm" fw={700} c="dark.9">
                                            {new Intl.NumberFormat('it-IT', { style: 'currency', currency }).format(total)}
                                        </Text>
                                    </Table.Td>

                                    {/* Stato */}
                                    <Table.Td style={{ width: 160 }}>
                                        <Badge 
                                            size="sm" 
                                            variant="light" 
                                            color={getStatusColor(order.status)}
                                            style={{ maxWidth: 'fit-content' }}
                                        >
                                            {formatStatus(order.status)}
                                        </Badge>
                                    </Table.Td>

                                    {/* Doc. Mexal */}
                                    <Table.Td style={{ width: 120 }}>
                                        <Text 
                                            size="sm" 
                                            fw={600} 
                                            style={{ fontFamily: 'monospace' }} 
                                            c={mexalDocuments?.[order.orderId] ? 'teal.7' : 'dimmed'}
                                        >
                                            {mexalDocuments?.[order.orderId] || '-'}
                                        </Text>
                                    </Table.Td>

                                    {/* Menu */}
                                    <Table.Td style={{ width: 50 }} onClick={(e) => e.stopPropagation()}>
                                        <Menu shadow="sm" width={200} position="bottom-end">
                                            <Menu.Target>
                                                <ActionIcon variant="transparent" color="gray">
                                                    <MaterialSymbol icon="more_vert" size={20} />
                                                </ActionIcon>
                                            </Menu.Target>
                                            <Menu.Dropdown>
                                                <Menu.Item leftSection={<MaterialSymbol icon="visibility" size={18} />}>
                                                    Visualizza dettagli
                                                </Menu.Item>
                                                <Menu.Item leftSection={<MaterialSymbol icon="print" size={18} />}>
                                                    Stampa
                                                </Menu.Item>
                                                <Menu.Divider />
                                                {/* Accetta ordine Mirakl */}
                                                {canAcceptOrder(order) && (
                                                    <>
                                                        <Menu.Item 
                                                            leftSection={<MaterialSymbol icon="check_circle" size={18} />}
                                                            color="teal"
                                                            onClick={() => handleAcceptOrder(order)}
                                                        >
                                                            Accetta ordine
                                                        </Menu.Item>
                                                        <Menu.Divider />
                                                    </>
                                                )}
                                                {/* Azioni Mexal */}
                                                {!mexalDocuments?.[order.orderId] && (
                                                    <>
                                                        {order.marketplace === 'EBAY' ? (
                                                            <Menu.Item 
                                                                leftSection={<MaterialSymbol icon="link" size={18} />}
                                                                color="blue"
                                                            >
                                                                Collega a Mexal
                                                            </Menu.Item>
                                                        ) : (order.marketplace === 'MEDIAWORLD' || order.marketplace === 'LEROY_MERLIN' || order.marketplace === 'LEROYMERLIN' || order.marketplace === 'EPRICE') ? (
                                                            <Menu.Item 
                                                                leftSection={<MaterialSymbol icon="add_circle" size={18} />}
                                                                color="green"
                                                                onClick={() => handleCreateMexalOrder(order)}
                                                            >
                                                                Crea ordine su Mexal
                                                            </Menu.Item>
                                                        ) : null}
                                                        <Menu.Divider />
                                                    </>
                                                )}
                                                <Menu.Item leftSection={<MaterialSymbol icon="cancel" size={18} />} color="red">
                                                    Annulla ordine
                                                </Menu.Item>
                                            </Menu.Dropdown>
                                        </Menu>
                                    </Table.Td>
                                </Table.Tr>

                                {/* Riga espansa - sempre presente per animazione */}
                                <Table.Tr className={classes.expandedRow}>
                                    <Table.Td colSpan={9} style={{ padding: 0, border: 'none', height: isExpanded ? 'auto' : 0, overflow: 'hidden' }}>
                                        <div 
                                            className={`${classes.expandedContent} ${isExpanded ? classes.expandedContentVisible : ''}`}
                                            style={{ '--marketplace-color': `var(--mantine-color-${getMarketplaceColor(order.marketplace)}-5)` } as React.CSSProperties}
                                        >
                                                <div className={classes.expandedInner}>
                                                    <div className={classes.detailsGrid}>
                                                        <div className={classes.detailGroup}>
                                                            <div className={classes.detailLabel}>Cliente</div>
                                                            <div className={classes.detailRow}><span className={classes.fieldLabel}>Nome:</span> <span className={classes.fieldValue}>{order.customer?.firstName}</span></div>
                                                            <div className={classes.detailRow}><span className={classes.fieldLabel}>Cognome:</span> <span className={classes.fieldValue}>{order.customer?.lastName}</span></div>
                                                            <div className={classes.detailRow}><span className={classes.fieldLabel}>Email:</span> <span className={classes.fieldValue}>{order.customer?.email || '-'}</span></div>
                                                            <div className={classes.detailRow}><span className={classes.fieldLabel}>Telefono:</span> <span className={classes.fieldValue}>{order.customer?.phone || '-'}</span></div>
                                                            {order.customer?.fiscalCode && (
                                                                <div className={classes.detailRow}><span className={classes.fieldLabel}>Codice Fiscale:</span> <span className={classes.fieldValue}>{order.customer.fiscalCode}</span></div>
                                                            )}
                                                            {order.customer?.vatNumber && (
                                                                <div className={classes.detailRow}><span className={classes.fieldLabel}>P.IVA:</span> <span className={classes.fieldValue}>{order.customer.vatNumber}</span></div>
                                                            )}
                                                            <div className={classes.detailRow} style={{ marginTop: 8 }}>
                                                                <span className={classes.fieldLabel}>Richiesta Fattura:</span> <span className={classes.fieldValue}>{order.invoiceRequested || order.isCompany ? 'Sì' : 'No'}</span>
                                                            </div>
                                                            {order.isCompany && (
                                                                <div className={classes.detailRow}><span className={classes.fieldLabel}>Tipo Cliente:</span> <span className={classes.fieldValue}>Business</span></div>
                                                            )}
                                                            {order.sdiCode && (
                                                                <div className={classes.detailRow}><span className={classes.fieldLabel}>Codice SDI:</span> <span className={classes.fieldValue}>{order.sdiCode}</span></div>
                                                            )}
                                                            {order.pecEmail && (
                                                                <div className={classes.detailRow}><span className={classes.fieldLabel}>PEC:</span> <span className={classes.fieldValue}>{order.pecEmail}</span></div>
                                                            )}
                                                            {order.billingAddress && (
                                                                <>
                                                                    <div className={classes.detailRow} style={{ marginTop: 8 }}><span className={classes.fieldLabel}>Indirizzo Fatturazione:</span></div>
                                                                    <div className={classes.detailRow}><span className={classes.fieldValue}>{order.billingAddress.street}</span></div>
                                                                    <div className={classes.detailRow}>
                                                                        <span className={classes.fieldValue}>
                                                                            {order.billingAddress.zipCode} {order.billingAddress.city}
                                                                            {order.billingAddress.state && ` (${order.billingAddress.state})`}
                                                                        </span>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                        
                                                        <div className={classes.detailGroup}>
                                                            <div className={classes.detailLabel}>Dati Spedizione</div>
                                                            {order.shippingAddress?.firstName && (
                                                                <>
                                                                    <div className={classes.detailRow}><span className={classes.fieldLabel}>Nome:</span> <span className={classes.fieldValue}>{order.shippingAddress.firstName}</span></div>
                                                                    <div className={classes.detailRow}><span className={classes.fieldLabel}>Cognome:</span> <span className={classes.fieldValue}>{order.shippingAddress.lastName}</span></div>
                                                                </>
                                                            )}
                                                            <div className={classes.detailRow}><span className={classes.fieldLabel}>Via:</span> <span className={classes.fieldValue}>{order.shippingAddress?.street}</span></div>
                                                            {order.shippingAddress?.street2 && (
                                                                <div className={classes.detailRow}><span className={classes.fieldLabel}>Note:</span> <span className={classes.fieldValue}>{order.shippingAddress.street2}</span></div>
                                                            )}
                                                            <div className={classes.detailRow}><span className={classes.fieldLabel}>CAP:</span> <span className={classes.fieldValue}>{order.shippingAddress?.zipCode}</span></div>
                                                            <div className={classes.detailRow}><span className={classes.fieldLabel}>Città:</span> <span className={classes.fieldValue}>{order.shippingAddress?.city}</span></div>
                                                            {order.shippingAddress?.state && (
                                                                <div className={classes.detailRow}><span className={classes.fieldLabel}>Provincia:</span> <span className={classes.fieldValue}>{order.shippingAddress.state}</span></div>
                                                            )}
                                                            <div className={classes.detailRow}><span className={classes.fieldLabel}>Nazione:</span> <span className={classes.fieldValue}>{order.shippingAddress?.country}</span></div>
                                                            {order.shippingAddress?.phone && (
                                                                <div className={classes.detailRow}><span className={classes.fieldLabel}>Telefono:</span> <span className={classes.fieldValue}>{order.shippingAddress.phone}</span></div>
                                                            )}
                                                            {order.shippingService && (
                                                                <div className={classes.detailRow} style={{ marginTop: 8 }}><span className={classes.fieldLabel}>Corriere:</span> <span className={classes.fieldValue}>{order.shippingService}</span></div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className={classes.detailLabel}>Articoli ({order.items?.length || 0})</div>
                                                    <div className={classes.itemsList}>
                                                        {order.items?.map((item, index) => {
                                                            const itemArticle = articlesMap.get(item.sku);
                                                            const itemImageUrl = itemArticle?.headerArticleDetails?.imageLink;
                                                            // Recupera disponibilità dall'articolo se presente, altrimenti usa quella dal backend
                                                            const availabilityStr = itemArticle?.headerArticleDetails?.availability;
                                                            const availableQuantity = availabilityStr 
                                                                ? parseInt(availabilityStr.replace(',', '.')) 
                                                                : item.availableQuantity || 0;
                                                            
                                                            return (
                                                                <div key={index} className={classes.itemRow}>
                                                                    {itemImageUrl ? (
                                                                        <img src={itemImageUrl} alt={item.productName} className={classes.itemImage} />
                                                                    ) : (
                                                                        <div className={classes.itemImagePlaceholder}>
                                                                            <MaterialSymbol icon="image" size={20} />
                                                                        </div>
                                                                    )}
                                                                    <div style={{ flex: 1 }}>
                                                                        <div className={classes.itemName}>{itemArticle?.headerArticleDetails?.title || item.productName}</div>
                                                                        <span className={classes.itemSku}>{item.sku}</span>
                                                                    </div>
                                                                    <div style={{ textAlign: 'right' }}>
                                                                        <Text size="sm" fw={700} c="dark.9">{item.quantity} pz</Text>
                                                                        <Text size="xs" fw={600} c={availableQuantity >= item.quantity ? 'teal.7' : 'red.7'}>
                                                                            {availableQuantity} disp.
                                                                        </Text>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </Table.Td>
                                    </Table.Tr>
                            </>
                        );
                    })}
                </Table.Tbody>
            </Table>
            {orders.length === 0 && !loading && (
                <Center py="xl">
                    <Text c="dimmed">Nessun ordine trovato</Text>
                </Center>
            )}

            {/* Modal creazione ordine Mexal */}
            <MexalOrderCreationModal
                opened={mexalModalOpen}
                onClose={handleMexalModalClose}
                order={orderForMexal}
                onSuccess={(docId) => {
                    console.log('Ordine Mexal creato:', docId);
                    // TODO: Refresh mexalDocuments
                }}
            />

            {/* Modal accettazione ordine Mirakl */}
            <MiraklAcceptOrderModal
                opened={acceptModalOpen}
                onClose={handleAcceptModalClose}
                order={orderForAccept}
                onSuccess={() => {
                    console.log('Ordine accettato');
                }}
            />
        </div>
    );
};

export default memo(OrderTable);
